import {modify} from "mythil/object";
import get from "lodash/get";
import set from "lodash/set";
import keys from "lodash/keys";
import size from "lodash/size";
import isEqual from "lodash/isEqual";
import omit from "lodash/omit";
import isNil from "lodash/isNil";
import merge from "lodash/merge";
import omitBy from "lodash/omitBy";
import mapValues from "lodash/mapValues";
import cloneDeep from "lodash/cloneDeep";
import { stateTrans } from "~/translations";
import { SETTINGS_ACTIONS } from "~/actions/settingsActions";
import { getEmptyFilterValues, getInitialFilterValues, getInitialValuesByFilterName, getModelTitle } from "../stateGetter";
import { presentFilteredViewsWithRelativeDates, presentRouteViews } from "../statePresenter";
import { getUniqueKey } from '~/lib/uniqueId';
import { getFitleredViewState } from "../StateGetters/FilteredViewStateGetters";
import { modelConfigActionTypes } from "../rx/modelConfigs";
import { prepareCurrentView, } from "../data/dataPreparer";
import { UI_ACTIONS } from "../actions/ui_actions";

const ROUTER_LOCATION_CHANGED = 'ROUTER_LOCATION_CHANGED';


export function reduceModelConfgsSettings(state, action) {
    const applySettings = (model) => {
        const { modelName, modelId, subviewName } = model,
              settingsIdentifier = subviewName || modelName,
              currentView = prepareCurrentView(state, { modelId, modelName: settingsIdentifier, refreshModel: action.changeRootModel }),
              activeView = { ...currentView ,},
              getCleanedFilteredView = (view) => omit(view, "previousTitle", "hasChanged"),
              isOwnView = !!get(getFitleredViewState(state, modelId).getFilteredViews(), activeView.viewKey);

        if (!isOwnView && !size(get(state.ui.settings, `${settingsIdentifier}.filteredViews`))) {
            activeView.viewKey = getUniqueKey()
        }

        
        return {
            ...model,
            activeView,
            settings: get(
                modify(state.ui.settings, settingsIdentifier, (modelSettings) => {
                    const { activeFilteredView, filteredViews, ...setting } = modelSettings || {}
                    return {
                        ...cloneDeep(setting),
                        activeFilteredView: activeView.viewKey,
                        filteredViews: size(filteredViews)
                            ? mapValues(filteredViews, (filteredView) => cloneDeep(getCleanedFilteredView(filteredView)))
                            : {
                                [activeView.viewKey]: getCleanedFilteredView(activeView)
                            }
                    }
                }), settingsIdentifier
            )
        }
    }
    
    switch (action.type) {
        case UI_ACTIONS.LOAD_MODEL_PATH: {
            mapValues(state.ui.loadedModels, (model, modelId) => {
                if (model.settings) { return; }
                if (!(model.modelName in state.models)) { return; }
                if ((get(state.models, `${model.modelName}.status`) !== "successfull")) { return; }

                state.ui.loadedModels = modify(state.ui.loadedModels, modelId, applySettings(model));
            })


            return state;
        }



        case modelConfigActionTypes.SUCCESS: {
            return modify(state, "ui.loadedModels", (loadedModels) => mapValues(loadedModels, (model) => {
                if (!(model.modelName in action.value)) {
                    return model;
                }

                if ("settings" in model) {
                    return model;
                }

                return applySettings(model);
            }));
        }
    }

    return state;
}


export function reduceFilteredViewsRoute(ui, action, state) {
    switch (action.type) {
        case ROUTER_LOCATION_CHANGED: {
            try {
                const routeView = presentRouteViews(state);
                if (!size(routeView)) { return ui; }

                ui.loadedModels = mapValues(ui.loadedModels, (model) => {
                    const {modelName, modelId} = model;
                    if (!routeView[modelName]) return model;
                    if (modelId !== ui.currentModelId) return model;

                    const view = presentFilteredViewsWithRelativeDates(routeView[modelName]);

                    if (!size(view)) { return model; }
                    if (isEqual(model.activeView, (view))) { return model; }

                    return modify(model, `activeView`, view)
                });
            } finally {
                return ui;
            }
        }
    }

    return ui;
}



export function reduceFilteredViews(ui, action, state) {
    if (!action.modelId) { return ui; }

    const getCurrentModel = (path) => get(ui, ["loadedModels", action.modelId].concat(path).filter(Boolean).join(".")),
        
    
        ensureBasePath = (path, defaultValue = null) => get(ui, path) ?? set(ui, path, defaultValue),
    

        modifyActiveView = (path, value) => {
            const base = `loadedModels.${action.modelId}.activeView`;
            path = [base, path].filter(Boolean).join(".")

            ui = modify(ui, `${base}.hasChanged`, true);

            ensureBasePath(path);
            return modify(ui, path, value);
        },
        
        
        modifySettings = (path, value) => {
            const basePath = `loadedModels.${action.modelId}.settings`;
            path = [basePath, path].filter(Boolean).join(".");

            ensureBasePath(path);
            return modify(ui, path, value);
        },
        
        
        maybeModifyActiveView = (path, value) => {
            const basePath = `loadedModels.${action.modelId}`;
            const activeView = get(ui, `${basePath}.activeView.viewKey`);


            if (action.view === activeView) { 
                return modifyActiveView(path, value)
            }

            path = ["filteredViews", action.view, path].filter(Boolean).join(".")

            return modifySettings(path, value)
        },
        
        
        modifySavedViews = (path, value, viewKey = action.view) => {
            const { parentModelId, settings } = getCurrentModel();
            const filteredViewState = getFitleredViewState(state, action.modelId);
            const tableSettingsIdentifier = filteredViewState.tableSettingsIdentifier;
            const base = `settings.${tableSettingsIdentifier}.filteredViews.${viewKey}`;

            if (!get(ui, `settings.${tableSettingsIdentifier}`)) {
                set(ui, `settings.${tableSettingsIdentifier}`, cloneDeep(settings))
            }

            ensureBasePath(base, { viewKey, title: getModelTitle(state, parentModelId || action.modelId) });
            path = [base, path].filter(Boolean).join(".");

            return modify(ui, path, value)
        };


    switch (action.type) {
        case SETTINGS_ACTIONS.SET_ACTIVE_FILTERED_VIEW:
            const filteredViewState = getFitleredViewState(state, action.modelId);
            let view = filteredViewState.getViewOrDefault(action.view.viewKey);
            view = presentFilteredViewsWithRelativeDates(view);

            ui = modifyActiveView("", omitBy({
                ...view,
                filters: view.filters && merge({}, getEmptyFilterValues(state, action.modelId), view.filters)
            }, isNil));

            ui = modifySettings("activeFilteredView", action.view.viewKey);
            ui = modify(ui, `settings.${getFitleredViewState(state, action.modelId).tableSettingsIdentifier}`, cloneDeep(getCurrentModel().settings));
            return ui;


        case SETTINGS_ACTIONS.RENAME_FILTERED_VIEW: {
            const { parentModelId } = getCurrentModel();
            ui = maybeModifyActiveView("", (view) => {
                return {
                    ...view,
                    title: action.title,
                    previousTitle: action.previousTitle ? action.previousTitle : (view.previousTitle || view.title || getModelTitle(state, parentModelId || action.modelId)),
                }
            });


            ui = modifySavedViews('title', (view) => {
                return action.title || view.previousTitle || getModelTitle(state, parentModelId || action.modelId)
            });

            ui = modifySettings(`filteredViews.${action.view}`, (view) => ({
                ...view,
                title: action.title,
            }));
            return ui;
        }

        case SETTINGS_ACTIONS.SHARE_FILTERED_VIEW:
            const isShared = !getCurrentModel(`settings.filteredViews.${action.view}.isShared`);
            ui = modifySavedViews('isShared', isShared);
            ui = maybeModifyActiveView("isShared", isShared);
            ui = modifySettings(`filteredViews.${action.view}.isShared`, isShared);
            return ui;


        case SETTINGS_ACTIONS.DUPLICATE_FILTERED_VIEW:{
            const initialFilters = getInitialFilterValues(state, action.modelId);
            const key = getUniqueKey(),
                  activeView = getFitleredViewState({...state, ui}, action.modelId).getCleanWorkingView(),
                  duplicateView = merge({}, activeView, {
                    viewKey: key,
                    isShared: false,
                    filters: mapValues(initialFilters, (value, key) => get(activeView.filters, key, value)),
                    createdAt: new Date().getTime(),
                    title: stateTrans(state, "views.copyTitle", { title: activeView.title }),
                  });

            ui = modifyActiveView("", { ...duplicateView })
            ui = modifySettings(`filteredViews.${key}`, { ...duplicateView });
            ui = modifySettings("activeFilteredView", key);
            ui = modify(ui, `settings.${getFitleredViewState(state, action.modelId).tableSettingsIdentifier}`, cloneDeep(getCurrentModel().settings));
            if (duplicateView.filters) { ui = modifyActiveView("filters", getCurrentModel("activeView.filters")) }
            return ui;
        }


        case SETTINGS_ACTIONS.DELETE_FILTERED_VIEW: {
            ui = modifySettings("", (settings) => {
                const { activeFilteredView, filteredViews } = settings,
                        shouldConfirmResetLastView = size(filteredViews) <= 1,
                        title = get(filteredViews, `${action.view}.title`),
                        shouldReset = shouldConfirmResetLastView && window.confirm(stateTrans(state, "views.confirmResetLastView", { title }));

                
                const { parentModelId, } = getCurrentModel();

                if (!shouldReset && shouldConfirmResetLastView) {
                    return settings;
                }

                const updatedFilteredViews = !shouldReset
                    ? omit(filteredViews, action.view)
                    : modify(filteredViews, action.view, {
                        viewKey: action.view,
                        createdAt: get(filteredViews, `${action.view}.createdAt`),
                        title: getModelTitle(state, parentModelId || action.modelId),
                    }),

                    updatedActiveView = shouldReset || activeFilteredView !== action.view ? activeFilteredView : keys(updatedFilteredViews)[0]


                return {
                    ...settings,
                    filteredViews: updatedFilteredViews,
                    activeFilteredView: updatedActiveView,
                }
            });


            ui = modifyActiveView("", (view) => {
                const { activeFilteredView, filteredViews } = getCurrentModel().settings;
                return omitBy({
                    ...cloneDeep(filteredViews[activeFilteredView]),
                    filters: filteredViews[activeFilteredView] && merge({}, getEmptyFilterValues(state, action.modelId), filteredViews[activeFilteredView])
                }, isNil)
            })

            ui = modify(ui, `settings.${getFitleredViewState(state, action.modelId).tableSettingsIdentifier}`, getCurrentModel().settings);
            return ui;
        }

        case SETTINGS_ACTIONS.CHANGE_COLUMN_ORDER: {
            let { columnOrder } = action;
            return modifyActiveView("columnOrder", columnOrder);
        };


        case SETTINGS_ACTIONS.CHANGE_SORT: {
            let { sort } = action;
            return modifyActiveView("sortOptions", sort);
        };


        case SETTINGS_ACTIONS.CHANGE_PER_PAGE: {
            let { perPage } = action;
            return modifyActiveView("perPage", perPage);
        }
            

        case SETTINGS_ACTIONS.RESET_COLUMNS: {
            const resetProperties = ["sortOptions", "columnVisibility", "columnWidths", "columnOrder"];
            resetProperties.forEach((prop) => {
                ui = modifyActiveView("", ({ ...view }) => {
                    delete view[prop];
                    return view;
                });
            });

            return ui;
        }


        case SETTINGS_ACTIONS.CHANGE_COLUMN_WIDTHS: {
            let { columnWidths } = action;
            return modifyActiveView("columnWidths", columnWidths);
        }


        case SETTINGS_ACTIONS.CHANGE_CALENDAR_VIEW_SETTINGS: {
            let { configName, value } = action;
            return modifyActiveView(`calendarViewConfig.${configName}`, value);
        }


        case SETTINGS_ACTIONS.CHANGE_COLUMN_VISIBILITY:
            let { columnName, visible } = action;
            return modifyActiveView(`columnVisibility.${columnName}`, visible);


        case SETTINGS_ACTIONS.UPDATE_FILTER: {
            return modifyActiveView("", (activeView) => {
                const emptyFilters = getEmptyFilterValues(state, action.modelId);
                if (action.props?.remove_filter) {
                    return {
                        ...activeView,
                        filters: merge({}, emptyFilters, omit(activeView.filters, action.name)),
                    };
                }

                let { modelId, name, props } = action,
                    { filters = {}, ...theView } = activeView,
                    initialValue = getInitialValuesByFilterName(state, modelId, name, "value"),
                    updatedProps = {
                        created_at: get(filters, `${name}.created_at`) ?? props.defaultCreatedAt ?? new Date().getTime(),
                        ...omit(props, "remove_filter", 'defaultCreatedAt', 'autoRemoveFilter'),
                        value: action.value !== undefined ? action.value : initialValue,
                    };
                    
                if (props.autoRemoveFilter && isEqual(updatedProps.value, initialValue)) {
                    updatedProps = get(emptyFilters, action.name);
                }
                    
                filters = set({ ...filters }, name, updatedProps);
                    
                return {
                    ...theView,
                    hasChanged: true,
                    filters: merge({}, emptyFilters, filters),
                }
            });
        }


        case SETTINGS_ACTIONS.UPDATE_FILTER_ORDER: {
            const filteredViewState = getFitleredViewState(state, action.modelId),
                  filters = filteredViewState.getCleanedWorkingFilters(),
                  tableSettingsIdentifier = filteredViewState.tableSettingsIdentifier;

            const createdAt = get(filters, `${action.name}.created_at`) ?? -1;
            if (!(createdAt + 1)) {
                return ui
            }

            ui = modifyActiveView("", (activeView) => ({
                ...activeView,
                filters: modify(activeView.filters, `${action.name}.created_at`, new Date().getTime())
            }));

            ui = modifySettings("", (settings) => {
                const filterOrder = settings.filterOrder ?? {};
                
                return {
                    ...settings,
                    filterOrder: modify(filterOrder, action.name, Date.now())
                }
            });

            if (!get(ui, `settings.${tableSettingsIdentifier}`)) { set(ui, `settings.${tableSettingsIdentifier}`) }
            ui = modify(ui, `settings.${tableSettingsIdentifier}`, getCurrentModel().settings);

            return ui;
        }


        case SETTINGS_ACTIONS.SAVE_VIEWS: {
            const isSubView = !!getCurrentModel().parentModelId;
            const filteredViewState = getFitleredViewState({ ...state, ui }, action.modelId)
            const tableSettingsIdentifier = filteredViewState.tableSettingsIdentifier;
            const updatedSettings = filteredViewState.getMergedSettings({ omitFilters: isSubView });
            ui = modify(ui, "settings", updatedSettings);
            ui = modifySettings("", cloneDeep(get(updatedSettings, tableSettingsIdentifier)));
            return ui;
        }
    }

    return ui;
}